import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import * as React from "react";
import { useCallback, useEffect, useState } from "react";
import { RootState, useAppDispatch, useAppSelector } from "../../../state/state.tsx";
import { SubscriptionDocWithId } from "../../Subscriptions/types/SubscriptionType.ts";
import { Box, Checkbox, CircularProgress, IconButton, MenuItem, TextField } from "@mui/material";
import { LiveSubscriptionType } from "../../Subscriptions/types/LiveSubscriptionTypes.ts";
import { getFutureCycles } from "../utilityFunctions.ts";
import { closeModal, toggleLoading } from "../../../state/ModalSlice/ModalSlice.ts";
import { useBackend } from "../../../hooks/backend/useBackend.ts";
import { toast } from "react-toastify";
import { SECRETS_ENV } from "../../../Config/firebase.ts";
import CloseIcon from "@mui/icons-material/Close";
import { differenceInDays, parseISO } from 'date-fns';
import SearchIcon from '@mui/icons-material/Search';
import Tooltip from '@mui/material/Tooltip';
import { selectEffectiveUID } from "../../../state/UserStatesV2/userSliceV2.ts";
import { ActionType } from "../../../enums.ts";
import { logAction } from '../utilityFunctions.ts';
import { Timestamp } from 'firebase/firestore';


const secretCloudTaskToken = import.meta.env.VITE_SECRET_CLOUD_TASK_TOKEN || process.env.VITE_SECRET_CLOUD_TASK_TOKEN


export default function RaiseInvoiceModal() {
    const Dispatch = useAppDispatch();
    //Form States
    const [selectedSubscription, setSelectedSubscription] = useState<number>(0);
    const [selectedMonth, setSelectedMonth] = useState<number>(0);
    const [monthOptions, setMonthOptions] = useState<string[]>([]);
    const [sendInvoiceList, setSendInvoiceList] = useState<string[]>([]);
    const [checkedState, setCheckedState] = useState<{ [key: string]: boolean }>({});
    const [searchQuery, setSearchQuery] = useState<string>("");
    const { uid: spId, display_name: spName } = useAppSelector((state: RootState) => state.userSliceV2?.data) || {}

    //Form Options
    const SubscriptionsList: SubscriptionDocWithId[] | null = useAppSelector((state: RootState) => state.subscriptionTableSlice.data)
    const safeSubscriptionsList = SubscriptionsList || [];

    useEffect(() => {
        const futureCycles = getFutureCycles();
        setMonthOptions(futureCycles.slice(0, 2));
    }, []);


    // Live subscription Documents Array
    const LiveSubscriptionDocumentArray: LiveSubscriptionType[] = useAppSelector((state: RootState) => state.LiveSubscriptionSlice.data) ?? []

    const FilteredLiveSubscriptions: LiveSubscriptionType[] = LiveSubscriptionDocumentArray.filter((docs) => {
        if (!docs) return false;

        // First check if this subscription matches the selected plan
        const isMatchingPlan = docs.subscriptionPlanRefId === safeSubscriptionsList[selectedSubscription]?.id;
        if (!isMatchingPlan) return false;

        // For MultiMonth or Count plans, show all matching subscriptions sorted by billingEnd
        if (safeSubscriptionsList[selectedSubscription]?.subscriptionType === 'MultiMonth' ||
            safeSubscriptionsList[selectedSubscription]?.subscriptionType === 'Count') {
            return true;
        }

        // For regular plans, show all matching subscriptions
        return true;
    }).sort((a, b) => {
        // Sort by billingEnd if it's a MultiMonth or Count subscription
        if (safeSubscriptionsList[selectedSubscription]?.subscriptionType === 'MultiMonth' ||
            safeSubscriptionsList[selectedSubscription]?.subscriptionType === 'Count') {
            if (!a.billingEnd || !b.billingEnd) return 0;
            return new Date(a.billingEnd).getTime() - new Date(b.billingEnd).getTime();
        }
        return 0;
    });

    const filteredAndSearchedSubscriptions = FilteredLiveSubscriptions.filter((doc) => {
        if (!searchQuery) return true;

        const searchLower = searchQuery.toLowerCase();
        return (
            doc.studentName?.toLowerCase().includes(searchLower) ||
            doc.phoneNumber?.toLowerCase().includes(searchLower)
        );
    });

    const getExpirationText = (billingEnd: string): string => {
        if (!billingEnd) return 'No expiration date';

        const today = new Date();
        try {
            const endDate = parseISO(billingEnd);
            const daysDifference = differenceInDays(endDate, today);

            if (daysDifference < 0) {
                return `Expired ${Math.abs(daysDifference)} days ago`;
            }
            else if (daysDifference === 0) {
                return `Expires Today`
            }
            else if (daysDifference <= 15) {
                return `Expires in ${daysDifference} days`;
            } else {
                // Format date as DD/MM/YYYY for dates more than 15 days away
                return `Valid until ${endDate.getDate().toString().padStart(2, '0')}/${(endDate.getMonth() + 1).toString().padStart(2, '0')}/${endDate.getFullYear()}`;
            }
        } catch (error) {
            console.error('Error parsing date:', billingEnd);
            return 'Invalid date';
        }
    };

    useEffect(() => {
        if (FilteredLiveSubscriptions) {
            const initialCheckedState: { [key: string]: boolean } = {};
            FilteredLiveSubscriptions.forEach((doc) => {
                initialCheckedState[doc.id] = checkForDefaultChecked(doc);
            });
            setCheckedState(initialCheckedState);

            setSendInvoiceList(FilteredLiveSubscriptions
                .filter(doc => initialCheckedState[doc.id])
                .map(doc => doc.id));
        }
    }, [LiveSubscriptionDocumentArray, selectedMonth, selectedSubscription]);

    function handleChangeSelectedSubscription(e: React.ChangeEvent<HTMLInputElement>) {
        setSelectedSubscription(Number(e.target.value))
    }

    function handleChangeSelectedMonth(e: React.ChangeEvent<HTMLInputElement>) {
        setSelectedMonth(Number(e.target.value))
    }

    function onChangeCheckboxClicked(e: React.ChangeEvent<HTMLInputElement>) {
        const { id, checked } = e.target;
        setCheckedState(prev => ({ ...prev, [id]: checked }));

        if (checked) {
            setSendInvoiceList(prev => [...prev, id]);
        } else {
            setSendInvoiceList(prev => prev.filter(item => item !== id));
        }
    }

    // Utility functions
    function checkIfInvoiceExists(doc: LiveSubscriptionType): boolean {
        if (doc.invoiceCycleList && doc.invoiceCycleList.includes(monthOptions[selectedMonth])) {
            return true
        } else {
            return false
        }
    }


    // New function for MultiMonth subscriptions
    function checkIfMultiMonthInvoiceExists(doc: LiveSubscriptionType): boolean {
        if (!doc.lastOrderStatus || !doc.lastOrderDate) {
            return false;
        }

        const daysSinceLastOrder = differenceInDays(new Date(), parseISO(doc.lastOrderDate));

        // Return true (invoice exists) if status is pending and order is less than 15 days old
        return doc.lastOrderStatus === 'Pending' && daysSinceLastOrder <= 15;

    }

    function checkIfCountInvoiceCanBeRaised(doc: LiveSubscriptionType): boolean {
        if (doc.remainingCount === undefined || !doc.billingEnd) {
            return false;
        }

        const daysUntilExpiration = differenceInDays(parseISO(doc.billingEnd), new Date());
        return doc.remainingCount <= 3 || daysUntilExpiration <= 15;
    }

    function checkifCheckboxDisabled(doc: LiveSubscriptionType): boolean {
        // Always disable if status is InActive or Terminated
        if (doc.status === "InActive" || doc.status === "Terminated" || doc.status === "Paused") {
            return true;
        }

        // Handle different subscription types
        if (safeSubscriptionsList[selectedSubscription]?.subscriptionType === 'MultiMonth') {
            if (!doc.billingEnd) {
                return true;
            }
            const daysUntilExpiration = differenceInDays(parseISO(doc.billingEnd), new Date());
            return !(daysUntilExpiration <= 15);
        } else if (safeSubscriptionsList[selectedSubscription]?.subscriptionType === 'Count') {
            if (doc.status !== "Active") {
                return true;
            }
            return !checkIfCountInvoiceCanBeRaised(doc);
        } else {
            if (doc.status !== "Active") {
                return true;
            }
            return checkIfInvoiceExists(doc);
        }
    }


    function checkForDefaultChecked(doc: LiveSubscriptionType): boolean {

        // Always disable if status is InActive
        if (doc.status === "InActive" || doc.status === "Terminated" || doc.status === "Paused") {
            return false;
        }

        // For non-MultiMonth plans, keep existing logic
        if (safeSubscriptionsList[selectedSubscription]?.subscriptionType !== 'MultiMonth' && safeSubscriptionsList[selectedSubscription]?.subscriptionType !== 'Count') {
            if (doc.status === "Active" || checkIfInvoiceExists(doc)) {
                return true;
            }
            return false;
        }

        if (safeSubscriptionsList[selectedSubscription]?.subscriptionType === 'Count') {
            const daysUntilExpiration = differenceInDays(parseISO(doc.billingEnd), new Date());
            if (doc.remainingCount <= 3 || daysUntilExpiration <= 15) {
                return true;
            }
            return false;
        }
        // For MultiMonth plans
        if (!doc.billingEnd) {
            return false;
        }

        const daysUntilExpiration = differenceInDays(parseISO(doc.billingEnd), new Date());

        if (!doc.lastOrderStatus || !doc.lastOrderDate) {
            return (daysUntilExpiration <= 15);
        }
        // Check same conditions as checkifCheckboxDisabled
        const condition1 = daysUntilExpiration <= 15;
        return condition1;
    }


    //Modal Handling Functions
    const open = useAppSelector((state: RootState) => state.ModalSlice.RaiseInvoiceModalOpen)

    function handleClose() {
        Dispatch(closeModal("RaiseInvoiceModalOpen"))
    }

    const providerId: string = useAppSelector(selectEffectiveUID)  
    const isLoading: boolean = useAppSelector((state: RootState) => state.ModalSlice.isLoading)
    const { axiosPostRequest } = useBackend("")

    async function handleRaiseInvoiceAPICall() {
        Dispatch(toggleLoading(true));

        try {
            let response;
            if (safeSubscriptionsList[selectedSubscription]?.subscriptionType === 'MultiMonth' || safeSubscriptionsList[selectedSubscription]?.subscriptionType === 'Count') {
                const multiMonthDoc = {
                    providerId: providerId,
                    subscriptionId: safeSubscriptionsList[selectedSubscription].id,
                    livesubscriptionIds: sendInvoiceList,
                    secretCloudTaskToken: secretCloudTaskToken
                };


                response = await axiosPostRequest(
                    `${SECRETS_ENV.API_END_POINT}/api/generateInvoiceMultiMonth`,
                    multiMonthDoc
                );
            } else {
                const regularDoc = {
                    ProviderID: providerId,
                    SubscriptionID: safeSubscriptionsList[selectedSubscription].id,
                    LiveSubscriptionIDs: sendInvoiceList,
                    monthYear: monthOptions[selectedMonth],
                    orderType: "online",
                };
                response = await axiosPostRequest(
                    `${SECRETS_ENV.API_END_POINT}/api/scheduleInvoices`,
                    regularDoc
                );
            }

            if (response.message !== "Request successful") {
                Dispatch(toggleLoading(false));
                return "error";
            }

            Dispatch(toggleLoading(false));
            return "success";
        } catch (error) {
            Dispatch(toggleLoading(false));
            return "error";
        }
    }

    // onsubmit handling

    async function onSubmit() {
        const response = await handleRaiseInvoiceAPICall()

        if (response === "success") {
            toast.success("Invoiced Raised Successfully!")
        } else {
            toast.error("Error Raising Invoice")
        }
        logAction({     
            action: ActionType.raiseInvoice,
            timestamp: Timestamp.now(),   
            platform: "web",
            performedBy: {
                spId: spId || "",
                name: spName || ""
            },
            context: {    
                invoicedLiveSubscriptions : sendInvoiceList,
                cycle: monthOptions[selectedMonth],
            },
        },providerId )
        handleClose()
    }

    function getDisabledTooltipMessage(doc: LiveSubscriptionType): string {
        if (doc.status === "InActive" || doc.status === "Terminated") {
            return "Cannot raise invoice for InActive/Terminated subscriptions";
        }
        if (doc.status === "Paused") {
            return "Cannot raise invoice for Paused subscriptions";
        }

        if (safeSubscriptionsList[selectedSubscription]?.subscriptionType === 'MultiMonth') {
            if (!doc.billingEnd) {
                return "Missing billing end date";
            }
            const daysUntilExpiration = differenceInDays(parseISO(doc.billingEnd), new Date());
            if (daysUntilExpiration > 15) {
                return "You can raise an invoice only within 15 days before the expiry date.";
            }
        } else if (safeSubscriptionsList[selectedSubscription]?.subscriptionType === 'Count') {
            if (doc.remainingCount === undefined || !doc.billingEnd) {
                return "Missing required information";
            }
            const daysUntilExpiration = differenceInDays(parseISO(doc.billingEnd), new Date());
            if (doc.remainingCount > 3 && daysUntilExpiration > 15) {
                return "You can raise an invoice only when remaining classes are less than 3 or within 15 days of expiry";
            }
        } else {
            if (doc.status !== "Active") {
                return `Cannot raise invoice for ${doc?.status?.toLowerCase()} subscriptions`;
            }
            if (checkIfInvoiceExists(doc)) {
                return "Invoice already exists for this month";
            }
        }

        return "";
    }

    const getPrice = (doc: LiveSubscriptionType) => {
        
        return doc.assignedPlans[doc.assignedPlanIndex || 0]?.price;
        
    }

    const MultiMonthSubscriptionRenderer = ({
        docs,
        checkedState,
        checkifCheckboxDisabled,
        onChangeCheckboxClicked
    }: {
        docs: LiveSubscriptionType;
        checkedState: { [key: string]: boolean };
        checkifCheckboxDisabled: (doc: LiveSubscriptionType) => boolean;
        onChangeCheckboxClicked: (e: React.ChangeEvent<HTMLInputElement>) => void;
    }) => {
        return (
            <div key={docs.id} className={"px-4 py-3 w-full flex relative items-center "}>
                <div className={"Avatar bg-cc_brand rounded-full size-10 flex justify-center items-center text-white font-thin text-cf_subHeading"}>
                    {docs.studentName[0]}
                </div>
                <div title={docs.studentName} className="flex ml-2 flex-col justify-between w-[40%] cursor-pointer">
                    <p className="font-normal overflow-hidden text-ellipsis whitespace-nowrap">
                        {docs.studentName}
                    </p>
                    <p className={`text-[14px] ${docs.status === "Active" ? "text-cc_success" :
                        docs.status === "Paused" ? "text-cc_bg-paused" :
                            "text-cc_error"
                        }`}>
                        {docs.status}
                    </p>
                    <p className="text-[14px]">
                        Rs.{getPrice(docs)} - {docs.assignedPlans[docs.assignedPlanIndex || 0]?.billingCycle}
                    </p>
                </div>
                <div className="absolute right-3 top-4 flex flex-col gap-1 justify-end items-end">
                    <Tooltip
                        title={checkifCheckboxDisabled(docs) ? getDisabledTooltipMessage(docs) : ""}
                        placement="top"
                        arrow
                    >
                        <div className={`flex justify-center h-4 ${checkifCheckboxDisabled(docs) ? "cursor-not-allowed" : "cursor-pointer"}`}>
                            <p className="text-cc_link text-cf_small underline-offset-1 ml-5">
                                {checkIfMultiMonthInvoiceExists(docs) ? "Invoice exists" : "Raise Invoice:"}
                            </p>
                            <Checkbox
                                id={docs.id}
                                checked={checkedState[docs.id] || false}
                                className={`${checkifCheckboxDisabled(docs) ? "cursor-not-allowed" : "cursor-pointer"}`}
                                disabled={checkifCheckboxDisabled(docs)}
                                onChange={onChangeCheckboxClicked}
                            />
                        </div>
                    </Tooltip>
                    {docs.billingEnd && docs.status !== "InActive" && <div>
                        <p className={`text-[14px] ${differenceInDays(parseISO(docs.billingEnd), new Date()) > 15
                            ? "text-cc_success"
                            : "text-cc_error"
                            }`}>
                            {getExpirationText(docs.billingEnd)}
                        </p>
                    </div>}

                    {docs.lastOrderDate && docs.lastOrderStatus ? (
                        <p className="text-[12px] text-gray-800 mt-1">
                            Last Order: {new Date(docs.lastOrderDate).toLocaleDateString('en-US', { month: 'short' })} {new Date(docs.lastOrderDate).getDate()} - <span className={`ml-1 ${docs.lastOrderStatus === 'Paid' ? 'text-cc_success' :
                                    docs.lastOrderStatus === 'Pending' ? 'text-orange-500' :
                                        'text-cc_error'
                                }`}>
                                {docs.lastOrderStatus}
                            </span>
                        </p>
                    ) : (
                        <p className="text-[12px] text-gray-800 mt-1 italic">
                            No previous orders
                        </p>
                    )}
                </div>

            </div>
        );
    };

    const RegularSubscriptionRenderer = ({
        docs,
        checkedState,
        checkifCheckboxDisabled,
        checkIfInvoiceExists,
        onChangeCheckboxClicked
    }: {
        docs: LiveSubscriptionType;
        checkedState: { [key: string]: boolean };
        checkifCheckboxDisabled: (doc: LiveSubscriptionType) => boolean;
        checkIfInvoiceExists: (doc: LiveSubscriptionType) => boolean;
        onChangeCheckboxClicked: (e: React.ChangeEvent<HTMLInputElement>) => void;
    }) => {
        return (
            <div key={docs.id} className={"px-4 py-3 w-full flex items-center relative"}>
                <div className={"Avatar bg-cc_brand rounded-full size-10 flex justify-center items-center text-white font-thin text-cf_subHeading"}>
                    {docs.studentName[0]}
                </div>
                <div title={docs.studentName} className="flex ml-2 gap-0.5 flex-col justify-between w-[40%] cursor-pointer">
                    <p className="font-normal overflow-hidden text-ellipsis whitespace-nowrap">
                        {docs.studentName}
                    </p>
                    <p className={`text-[14px] ${docs.status === "Active" ? "text-cc_success" :
                        docs.status === "Paused" ? "text-cc_bg-paused" :
                            "text-cc_error"
                        }`}>
                        {docs.status}
                    </p>
                    <p className="text-[14px]">
                        Rs.{docs.Price - docs.discountNegotiated}
                    </p>
                </div>
                <Tooltip
                    title={checkifCheckboxDisabled(docs) ? getDisabledTooltipMessage(docs) : ""}
                    placement="top"
                    arrow
                >
                    <div className={`absolute right-3 top-8 flex justify-center h-4 ${checkifCheckboxDisabled(docs) ? "cursor-not-allowed" : "cursor-pointer"}`}>
                        <p className="text-cc_link text-cf_small underline-offset-1 ml-5">
                            {checkIfInvoiceExists(docs) ? "Invoice exists" : "Raise Invoice:"}
                        </p>
                        <Checkbox
                            id={docs.id}
                            checked={checkedState[docs.id] || false}
                            className={`${checkifCheckboxDisabled(docs) ? "cursor-not-allowed" : "cursor-pointer"}`}
                            disabled={checkifCheckboxDisabled(docs)}
                            onChange={onChangeCheckboxClicked}
                        />
                    </div>
                </Tooltip>
            </div>
        );
    };

    const CountSubscriptionRenderer = ({
        docs,
        checkedState,
        checkifCheckboxDisabled,
        onChangeCheckboxClicked
    }: {
        docs: LiveSubscriptionType;
        checkedState: { [key: string]: boolean };
        checkifCheckboxDisabled: (doc: LiveSubscriptionType) => boolean;
        onChangeCheckboxClicked: (e: React.ChangeEvent<HTMLInputElement>) => void;
    }) => {
        return (
            <div key={docs.id} className={"px-4 py-3 w-full flex relative items-center "}>
                <div className={"Avatar bg-cc_brand rounded-full size-10 flex justify-center items-center text-white font-thin text-cf_subHeading"}>
                    {docs.studentName[0]}
                </div>
                <div title={docs.studentName} className="flex ml-2 flex-col justify-between w-[40%] cursor-pointer">
                    <p className="font-normal overflow-hidden text-ellipsis whitespace-nowrap">
                        {docs.studentName}
                    </p>
                    <p className={`text-[14px] ${docs.status === "Active" ? "text-cc_success" :
                        docs.status === "Paused" ? "text-cc_bg-paused" :
                            "text-cc_error"
                        }`}>
                        {docs.status}
                    </p>
                    <p className="text-[12px] mt-1">
                        Rs.{getPrice(docs)} : {docs.remainingCount % 1 === 0 ? docs.remainingCount : docs.remainingCount.toFixed(2)} classes remaining
                    </p>
                </div>
                <div className="absolute right-3 top-4 flex flex-col gap-1 justify-end items-end">
                    <Tooltip
                        title={checkifCheckboxDisabled(docs) ? getDisabledTooltipMessage(docs) : ""}
                        placement="top"
                        arrow
                    >
                        <div className={`flex justify-center h-4 ${checkifCheckboxDisabled(docs) ? "cursor-not-allowed" : "cursor-pointer"}`}>
                            <p className="text-cc_link text-cf_small underline-offset-1 ml-5">
                                Raise Invoice:
                            </p>
                            <Checkbox
                                id={docs.id}
                                checked={checkedState[docs.id] || false}
                                className={`${checkifCheckboxDisabled(docs) ? "cursor-not-allowed" : "cursor-pointer"}`}
                                disabled={checkifCheckboxDisabled(docs)}
                                onChange={onChangeCheckboxClicked}
                            />
                        </div>
                    </Tooltip>
                    {docs.billingEnd && docs.status !== "InActive" && <div>
                        <p className={`text-[14px] ${differenceInDays(parseISO(docs.billingEnd), new Date()) > 15
                            ? "text-cc_success"
                            : "text-cc_error"
                            }`}>
                            {getExpirationText(docs.billingEnd)}
                        </p>
                    </div>}
                    {docs.lastOrderDate && docs.lastOrderStatus ? (
                        <p className="text-[12px] text-gray-800 mt-1">
                            Last Order: {new Date(docs.lastOrderDate).toLocaleDateString('en-US', { month: 'short' })} {new Date(docs.lastOrderDate).getDate()} - <span className={`ml-1 ${docs.lastOrderStatus === 'Paid' ? 'text-cc_success' :
                                    docs.lastOrderStatus === 'Pending' ? 'text-orange-500' :
                                        'text-cc_error'
                                }`}>
                                {docs.lastOrderStatus}
                            </span>
                        </p>
                    ) : (
                        <p className="text-[12px] text-gray-800 mt-1 italic">
                            No previous orders
                        </p>
                    )}
                </div>
            </div>
        );
    };

    // Add event handler for ESC key
    const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Escape') {
            setSearchQuery('');
        }
    };

    return (<div>
        <Dialog

            maxWidth="md"
            // fullWidth={true}
            open={open}
            sx={{
                padding: 0,
                '& .MuiPaper-root': {
                    borderRadius: '10px',
                },
            }}
        >

            <DialogContent sx={{ paddingY: "50px", paddingX: "60px" }} className={"bg-white"}>
                <div className={"header pb-[20px]"}>
                    <p className={"text-cf_heading font-[600] "}>Generate Monthly Bill</p>
                    <p className={"text-cf_small font-[400] "}>Generate the bill and invoice payment link will be
                        send via whatsapp</p>
                </div>
                <Box
                    component={"div"}
                    sx={{}}
                >
                    <div className={"p-2 rounded-lg border-cc_modal-grey-border border-[1px] w-[550px]"}>

                        <div className={"absolute right-4 top-4"}><IconButton onClick={handleClose}>
                            <CloseIcon fontSize={"medium"} />
                        </IconButton></div>

                        <div className={""}>
                            <div className={"flex flex-col gap-2.5"}>
                                <TextField
                                    size={"small"}
                                    id="SelectSubscription"
                                    select
                                    fullWidth={true}
                                    label="Subscription Plan"
                                    value={selectedSubscription}
                                    onChange={handleChangeSelectedSubscription}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            '& fieldset': {
                                                borderColor: 'grey', borderRadius: '10px'// Default border color
                                            }, '&:hover fieldset': {
                                                borderColor: 'grey', // Border color on hover
                                            }, '&.Mui-focused fieldset': {
                                                borderColor: 'skyblue', // Border color when focused
                                            }, '& .MuiInputBase-input': {
                                                fontSize: '14px', // Setting font size
                                            },
                                        }, fontWeight: 400, fontSize: "1px",
                                    }}
                                >
                                    {safeSubscriptionsList.length > 0 ? safeSubscriptionsList.map((option, index) => (
                                        option.subscriptionType === 'Count' ? (
                                            <MenuItem key={index} value={index}>
                                                {option.Name} - Count
                                            </MenuItem>
                                        ) : option.subscriptionType === 'MultiMonth' ? (
                                            <MenuItem key={index} value={index}> {option.Name} - MultiMonth</MenuItem>
                                        ) : (
                                            <MenuItem key={index} value={index}>
                                                {option.Name} - ₹{option.Price}
                                            </MenuItem>
                                        ))) : <MenuItem key="0" value={0}>
                                        No Options Found
                                    </MenuItem>}

                                </TextField>

                                {safeSubscriptionsList[selectedSubscription]?.subscriptionType !== 'MultiMonth' && safeSubscriptionsList[selectedSubscription]?.subscriptionType !== 'Count' && <TextField
                                    size={"small"}
                                    id="SelectMonth"
                                    select
                                    fullWidth={true}
                                    label="Select Month"
                                    value={selectedMonth}
                                    onChange={handleChangeSelectedMonth}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            '& fieldset': {
                                                borderColor: 'grey', borderRadius: '10px'// Default border color
                                            }, '&:hover fieldset': {
                                                borderColor: 'grey', // Border color on hover
                                            }, '&.Mui-focused fieldset': {
                                                borderColor: 'skyblue', // Border color when focused
                                            }, '& .MuiInputBase-input': {
                                                fontSize: '14px', // Setting font size
                                            },
                                        }, fontWeight: 400, fontSize: "1px",
                                    }}
                                >
                                    {monthOptions.length > 0 ? monthOptions.map((option, index) => (
                                        <MenuItem key={index} value={index}>
                                            {option}
                                        </MenuItem>)) : <MenuItem key="0" value={0}>
                                        No Options Found
                                    </MenuItem>}


                                </TextField>}
                            </div>
                            <div className={"h-0 border-cc_modal-grey-border mt-2 border-[1px] w-full"}></div>

                            <div className="relative my-2">
                                <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                                <TextField
                                    size="small"
                                    fullWidth
                                    placeholder="Search by name or phone number"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    onKeyDown={handleSearchKeyDown}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            paddingLeft: '40px',
                                            '& fieldset': {
                                                borderColor: 'grey',
                                                borderRadius: '10px'
                                            },
                                            '&:hover fieldset': {
                                                borderColor: 'grey',
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: 'skyblue',
                                            },
                                            '& .MuiInputBase-input': {
                                                fontSize: '14px',
                                            },
                                        }
                                    }}
                                />
                            </div>

                            {filteredAndSearchedSubscriptions.length > 0 ? (
                                <div>
                                    {filteredAndSearchedSubscriptions.map((docs: LiveSubscriptionType) => {
                                        if (safeSubscriptionsList[selectedSubscription]?.subscriptionType === 'MultiMonth') {
                                            return (
                                                <MultiMonthSubscriptionRenderer
                                                    key={docs.id}
                                                    docs={docs}
                                                    checkedState={checkedState}
                                                    checkifCheckboxDisabled={checkifCheckboxDisabled}
                                                    onChangeCheckboxClicked={onChangeCheckboxClicked}
                                                />
                                            );
                                        } else if (safeSubscriptionsList[selectedSubscription]?.subscriptionType === 'Count') {
                                            return (
                                                <CountSubscriptionRenderer
                                                    key={docs.id}
                                                    docs={docs}
                                                    checkedState={checkedState}
                                                    checkifCheckboxDisabled={checkifCheckboxDisabled}
                                                    onChangeCheckboxClicked={onChangeCheckboxClicked}
                                                />
                                            );
                                        } else {
                                            return (
                                                <RegularSubscriptionRenderer
                                                    key={docs.id}
                                                    docs={docs}
                                                    checkedState={checkedState}
                                                    checkifCheckboxDisabled={checkifCheckboxDisabled}
                                                    checkIfInvoiceExists={checkIfInvoiceExists}
                                                    onChangeCheckboxClicked={onChangeCheckboxClicked}
                                                />
                                            );
                                        }
                                    })}
                                </div>
                            ) : (
                                <div className={"flex flex-col items-center p-4 justify-center"}>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24"
                                        fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                                        strokeLinejoin="round" className="feather feather-info">
                                        <circle cx="12" cy="12" r="10"></circle>
                                        <line x1="12" y1="16" x2="12" y2="12"></line>
                                        <line x1="12" y1="8" x2="12.01" y2="8"></line>
                                    </svg>
                                    {searchQuery ? 'No matching subscriptions found' : 'No Users in Subscription!'}
                                </div>
                            )}

                        </div>

                    </div>
                </Box>

                <div>

                </div>
                <div className={"flex gap-2 pt-2"}>
                    <button onClick={handleClose} disabled={isLoading}
                        className={`w-full bg-cc_bg-tertiary border border-solid border-gray-500 p-1.5 rounded-lg ${isLoading ? "cursor-not-allowed" : "cursor-pointer"}`}>Cancel
                    </button>
                    <button
                        onClick={onSubmit} disabled={isLoading}
                        className={`w-full bg-cc_brand text-white p-1.5 rounded-lg border-gray-500 flex justify-center items-center ${isLoading ? "cursor-not-allowed" : "cursor-pointer"}`}>

                        {!isLoading && "Send Invoice"}

                        {isLoading && <CircularProgress sx={{ color: "white" }} size={20} />}
                        <p className={"ml-2"}>{isLoading ? "Loading" :
                            <div className={"ml-1"}>
                                <svg width="17" height="16" viewBox="0 0 17 16" fill="none"
                                    xmlns="http://www.w3.org/2000/svg">
                                    <path fillRule="evenodd" clipRule="evenodd"
                                        d="M8.5 15.5C12.366 15.5 15.5 12.366 15.5 8.5C15.5 4.63401 12.366 1.5 8.5 1.5C4.63401 1.5 1.5 4.63401 1.5 8.5C1.5 9.75544 1.8305 10.9337 2.40924 11.9525L1.5 15.5L5.15743 14.6519C6.15069 15.1927 7.28946 15.5 8.5 15.5ZM8.5 14.4231C11.7712 14.4231 14.4231 11.7712 14.4231 8.5C14.4231 5.22878 11.7712 2.57692 8.5 2.57692C5.22878 2.57692 2.57692 5.22878 2.57692 8.5C2.57692 9.76303 2.97225 10.9337 3.64592 11.8951L3.11538 13.8846L5.13997 13.3785C6.09468 14.0373 7.25229 14.4231 8.5 14.4231Z"
                                        fill="#BFC8D0" />
                                    <path
                                        d="M14.5 8C14.5 11.3137 11.8137 14 8.5 14C7.23608 14 6.06344 13.6092 5.09633 12.9418L3.04545 13.4545L3.58288 11.4392C2.90046 10.4653 2.5 9.27944 2.5 8C2.5 4.68629 5.18629 2 8.5 2C11.8137 2 14.5 4.68629 14.5 8Z"
                                        fill="url(#paint0_linear_348_16324)" />
                                    <path fillRule="evenodd" clipRule="evenodd"
                                        d="M8.5 15C12.366 15 15.5 11.866 15.5 8C15.5 4.13401 12.366 1 8.5 1C4.63401 1 1.5 4.13401 1.5 8C1.5 9.25544 1.8305 10.4337 2.40924 11.4525L1.5 15L5.15743 14.1519C6.15069 14.6927 7.28946 15 8.5 15ZM8.5 13.9231C11.7712 13.9231 14.4231 11.2712 14.4231 8C14.4231 4.72878 11.7712 2.07692 8.5 2.07692C5.22878 2.07692 2.57692 4.72878 2.57692 8C2.57692 9.26303 2.97225 10.4337 3.64592 11.3951L3.11538 13.3846L5.13997 12.8785C6.09468 13.5373 7.25229 13.9231 8.5 13.9231Z"
                                        fill="white" />
                                    <path
                                        d="M6.75001 4.7497C6.58358 4.41541 6.32826 4.44501 6.07034 4.44501C5.60938 4.44501 4.89062 4.99715 4.89062 6.02473C4.89063 6.8669 5.26172 7.78877 6.51221 9.16781C7.71902 10.4987 9.30469 11.1872 10.6211 11.1637C11.9375 11.1403 12.2083 10.0075 12.2083 9.62492C12.2083 9.45535 12.1031 9.37075 12.0306 9.34777C11.582 9.13248 10.7547 8.73133 10.5664 8.65596C10.3782 8.58059 10.2799 8.68253 10.2188 8.73799C10.048 8.90068 9.70963 9.38015 9.59376 9.48799C9.47789 9.59583 9.30514 9.54125 9.23325 9.50048C8.96872 9.39433 8.25146 9.07529 7.67973 8.52107C6.97266 7.83564 6.93117 7.59983 6.79795 7.38991C6.69138 7.22198 6.76958 7.11894 6.8086 7.07392C6.96095 6.89814 7.1713 6.62675 7.26564 6.49189C7.35997 6.35702 7.28508 6.15226 7.24015 6.02473C7.04689 5.47626 6.88316 5.01713 6.75001 4.7497Z"
                                        fill="white" />
                                    <defs>
                                        <linearGradient id="paint0_linear_348_16324" x1="13.75" y1="3.5" x2="2.5"
                                            y2="14"
                                            gradientUnits="userSpaceOnUse">
                                            <stop stopColor="#5BD066" />
                                            <stop offset="1" stopColor="#27B43E" />
                                        </linearGradient>
                                    </defs>
                                </svg>
                            </div>}</p>
                    </button>

                </div>
            </DialogContent>
        </Dialog>
    </div>)
}